import { useCallback } from 'react';

import { useMutation, UseMutationResult } from '@eika-infrastruktur/react-query';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import ANALYTICS_CATEGORIES from '../../../../analytics/categories';
import useAccountChange from '../../../../fetchHooks/useAccountChange';
import { useRefreshLoanRepaymentPlan } from '../../../../fetchHooks/useLoanRepaymentPlan';
import { useRefreshLoanDetails } from '../../../../fetchHooks/useLoans';
import type { Account, AccountId } from '../../../../generated-types';
import { AnalyticsEvents, trackEvent } from '../../../../services/analytics';
import { createDownpayment } from '../../../../services/loanService';
import { DownpaymentState } from './state';

export type DownpaymentPayload = {
    loanAccount: Account;
} & DownpaymentState;

const mapDownpaymentRequest = ({ amount, fromAccount, note, loanAccount }: DownpaymentPayload) => ({
    fromAccount: fromAccount ?? { value: '' },
    toAccount: loanAccount.accountNumber,
    amount: amount ?? 0,
    note: note ?? '',
});

export type DownpaymentMutation = {
    updateOnChange: (fromAccountId?: AccountId, loanAccountId?: AccountId) => void;
} & UseMutationResult<void, RequestExceptionWithResponse, DownpaymentPayload>;

const useCreateDownpayment = (
    targetFromAccount: Account | undefined,
    targetToAccount: Account | undefined,
): DownpaymentMutation => {
    const refreshAccounts = useAccountChange();
    const refreshLoanDetails = useRefreshLoanDetails();
    const refreshRepaymentPlan = useRefreshLoanRepaymentPlan();

    const updateOnChange = useCallback(
        (fromAccountId?: AccountId, loanAccountId?: AccountId) => {
            if (fromAccountId && loanAccountId) {
                refreshAccounts([fromAccountId, loanAccountId]);
                refreshLoanDetails();
                refreshRepaymentPlan(loanAccountId);
            }
        },
        [refreshLoanDetails, refreshAccounts, refreshRepaymentPlan],
    );

    const mutation = useMutation<void, RequestExceptionWithResponse, DownpaymentPayload>({
        mutationFn: (payload) => createDownpayment(payload.loanAccount.accountId, mapDownpaymentRequest(payload)),
        onSuccess: () => {
            trackEvent(AnalyticsEvents.DOWNPAYMENT, ANALYTICS_CATEGORIES.DOWNPAYMENT, 'create_downpayment', 'complete');
            updateOnChange(targetFromAccount?.accountId, targetToAccount?.accountId);
        },
        onError: () => {
            trackEvent(AnalyticsEvents.DOWNPAYMENT, ANALYTICS_CATEGORIES.DOWNPAYMENT, 'create_downpayment', 'error');
        },
    });

    return { ...mutation, updateOnChange };
};

export default useCreateDownpayment;
